import { type ReactElement, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { type InquiryAnswers, useSubmitInquiry } from '@/features/submit-inquiry';
import { BUDGET_RANGES, HEADCOUNTS, INQUIRY_TYPES, TIMEFRAMES, WORK_TYPES } from '@/shared/config';
import { Honeypot } from '@/shared/ui/honeypot';

const OTHER = '기타';

const selectClass =
  'h-11 rounded-md border border-border bg-card px-3 text-sm ' +
  'focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary';

const labelClass = 'text-[12.5px] font-medium text-muted-foreground';

function Select({
  id,
  name,
  label,
  options,
  defaultValue = '',
  hint,
}: {
  id: string;
  /** 서버로 보낼 때 쓰는 이름. 화면의 id 와 달리 이쪽은 계약이다. */
  name: string;
  label: string;
  options: readonly string[];
  defaultValue?: string;
  hint?: string;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <select id={id} name={name} className={selectClass} defaultValue={defaultValue}>
        <option value="" disabled>
          선택해 주세요
        </option>
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
      {hint ? <span className="text-[12px] text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

/** 문의 응대 목적의 개인정보 동의. 회원가입 동의와 **목적이 다르므로** 여기서 따로 받는다. */
function InquiryConsent({
  agreed,
  onChange,
}: {
  agreed: boolean;
  onChange: (next: boolean) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-4">
      <label htmlFor="edu-consent" className="flex items-start gap-2 text-[13px]">
        <input
          id="edu-consent"
          type="checkbox"
          checked={agreed}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-primary"
        />
        <span>
          <span className="text-muted-foreground">[필수]</span> 문의 응대를 위한 개인정보
          수집·이용에 동의합니다
        </span>
      </label>
      <dl className="flex flex-col gap-1 rounded-sm bg-secondary px-3 py-2 text-[12px] text-muted-foreground">
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 수집 항목</dt>
          <dd>담당자명, 이메일, 연락처, 회사·기관명</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 이용 목적</dt>
          <dd>교육 문의 응대 및 제안</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 보유 기간</dt>
          <dd>문의 처리 완료 후 1년</dd>
        </div>
      </dl>
      <p className="text-[12px] text-muted-foreground">
        동의를 거부하실 수 있으나, 거부하시면 문의를 접수할 수 없습니다.{' '}
        <Link to="/privacy" className="underline underline-offset-2">
          개인정보처리방침
        </Link>
      </p>
    </div>
  );
}

/** 서버가 준 문구를 그대로 보여준다. 화면이 지어내면 사유가 흐려진다 (로그인 화면과 같은 방식). */
const FALLBACK_MESSAGE = '문의를 접수하지 못했습니다. 잠시 후 다시 시도해 주세요';

const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

/** 빈 칸은 값이 아니다 — 고르지 않은 select 는 빈 문자열을 담고 있다. */
const filled = (data: FormData, key: string): string | undefined => {
  const value = data.get(key);
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

/**
 * 폼에 적힌 값을 서버가 받는 모양으로 옮긴다.
 *
 * 칸마다 `useState` 를 두지 않는 이유 — 입력이 열두 개인 화면에서 상태를 열두 개 두면
 * 글자 하나마다 폼 전체가 다시 그려지고, 정작 필요한 것은 보낼 때의 값 한 벌뿐이다.
 */
const toAnswers = (form: HTMLFormElement, source: string | undefined): InquiryAnswers => {
  const data = new FormData(form);

  return {
    contactName: filled(data, 'contactName') ?? '',
    email: filled(data, 'email') ?? '',
    agreedToPrivacy: true,
    inquiryType: filled(data, 'inquiryType'),
    organization: filled(data, 'organization'),
    phone: filled(data, 'phone'),
    headcount: filled(data, 'headcount'),
    timeframe: filled(data, 'timeframe'),
    budgetRange: filled(data, 'budgetRange'),
    workTypes: data.getAll('work').filter((value): value is string => typeof value === 'string'),
    workTypeOther: filled(data, 'workTypeOther'),
    message: filled(data, 'message'),
    source,
    homepage: filled(data, 'homepage'),
  };
};

function InquirySent(): ReactElement {
  return (
    <div className="u-container flex max-w-[34rem] flex-col items-center gap-5 py-24 text-center">
      <span aria-hidden="true" className="text-[2rem]">
        ✓
      </span>
      <h1 className="text-[1.375rem] font-bold tracking-[-0.04em]">문의가 접수되었습니다</h1>
      <p className="text-[14px] leading-[1.8] text-muted-foreground">
        영업일 기준 2일 안에 남겨 주신 이메일로 답변드리겠습니다.
      </p>
      <Link
        to="/library"
        className="inline-flex h-11 items-center rounded-md border border-foreground px-5 text-sm font-medium"
      >
        그동안 무료 자료 보기
      </Link>
    </div>
  );
}

/**
 * 교육 문의 폼 — 1차 오픈 범위 §6.3-1.
 *
 * 랜딩(`/education`)과 **별도 페이지**다. 랜딩은 설득이 일이고 이 화면은 입력이 일이라,
 * 한 페이지에 두면 둘 다 흐려진다. 여기 온 사람은 이미 설득된 상태이므로
 * 딴 데로 새게 하지 않는다 — 링크를 최소로 둔다.
 */
export function EducationInquiryPage(): ReactElement {
  const [params] = useSearchParams();
  // 지역·기관 배너에서 넘어온 사람은 이미 기관 문의임을 밝혔다 — 다시 고르게 하지 않는다
  const presetType = params.get('type') === 'institution' ? '공공기관·지자체 교육' : '';
  // 어느 배너를 보고 왔는지. 담당자가 맥락을 갖고 연락하게 하는 값이라 함께 보낸다.
  const source = params.get('type') ?? undefined;

  const [agreed, setAgreed] = useState(false);
  const [otherWork, setOtherWork] = useState('');
  const [wantsOtherWork, setWantsOtherWork] = useState(false);
  const submitInquiry = useSubmitInquiry();

  // 접수 여부를 화면이 따로 기억하지 않는다 — 서버가 받았다는 사실이 곧 접수다.
  //   로컬 상태로 두면 요청이 실패해도 「접수되었습니다」가 뜰 수 있다.
  if (submitInquiry.isSuccess) return <InquirySent />;

  return (
    <div className="u-container flex max-w-[44rem] flex-col gap-8 py-12">
      <nav aria-label="현재 위치" className="text-[12.5px] text-muted-foreground">
        <Link to="/education" className="underline-offset-4 hover:underline">
          교육
        </Link>
        <span aria-hidden="true"> / </span>
        <span>문의하기</span>
      </nav>

      <header className="flex flex-col gap-3">
        <h1 className="text-[clamp(1.5rem,1.2rem+1.2vw,2rem)] font-bold tracking-[-0.042em]">
          교육 문의
        </h1>
        <p className="text-[14.5px] leading-[1.8] text-muted-foreground">
          지금 어떤 업무에 시간이 가장 많이 드는지 알려 주세요. 남겨 주신 내용이 사전 진단의
          출발점이 됩니다.
        </p>
      </header>

      <form
        className="flex flex-col gap-6 rounded-lg border border-border p-6"
        aria-busy={submitInquiry.isPending}
        onSubmit={(event) => {
          event.preventDefault();
          // 잠금을 버튼의 `disabled` 에 맡기지 않는다 — 방금 누른 버튼이 곧 포커스를 가진
          //   요소라, 그것을 비활성화하면 브라우저가 포커스를 문서 밖으로 날린다.
          //   키보드·화면낭독기 사용자는 실패 문구가 떠도 그것을 찾지 못한다.
          //   그래서 알림은 aria-disabled 로 하고, 실제 차단은 이 줄이 한다.
          if (!agreed || submitInquiry.isPending) {
            return;
          }
          submitInquiry.submit(toAnswers(event.currentTarget, source));
        }}
      >
        <Honeypot id="edu-homepage" />

        <div className="grid gap-4 md:grid-cols-2">
          <Select
            id="edu-type"
            name="inquiryType"
            label="문의 유형"
            options={INQUIRY_TYPES}
            defaultValue={presetType}
          />
          <Field id="edu-org" name="organization" label="회사·기관명" placeholder="오톱 주식회사" />
          <Field id="edu-name" name="contactName" label="담당자명" placeholder="홍길동" required />
          <Field
            id="edu-email"
            name="email"
            type="email"
            label="이메일"
            placeholder="name@example.com"
            required
          />
          <Field id="edu-phone" name="phone" label="연락처" placeholder="010-0000-0000" />
          <Select id="edu-headcount" name="headcount" label="예상 인원" options={HEADCOUNTS} />
          <Select id="edu-timeframe" name="timeframe" label="희망 시기" options={TIMEFRAMES} />
          <Select id="edu-budget" name="budgetRange" label="예산 범위" options={BUDGET_RANGES} />
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className={labelClass}>자동화하고 싶은 업무 (복수 선택)</legend>
          <div className="flex flex-wrap gap-2 pt-1">
            {WORK_TYPES.map((work) => (
              <label
                key={work}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-[13.5px] has-checked:border-foreground"
              >
                <input
                  type="checkbox"
                  name="work"
                  value={work}
                  checked={work === OTHER ? wantsOtherWork : undefined}
                  onChange={
                    work === OTHER ? (event) => setWantsOtherWork(event.target.checked) : undefined
                  }
                  className="size-4 accent-primary"
                />
                {work}
              </label>
            ))}
          </div>
          {/* 「기타」로 끝내면 아무 정보도 안 남는다 — 실제로 쓸 칸을 연다 */}
          {wantsOtherWork ? (
            <div className="flex flex-col gap-1.5 pt-1">
              <label htmlFor="edu-work-other" className={labelClass}>
                어떤 업무인지 알려주세요
              </label>
              <input
                id="edu-work-other"
                name="workTypeOther"
                type="text"
                maxLength={60}
                value={otherWork}
                placeholder="예: 재고 정리, 계약서 검토"
                onChange={(event) => setOtherWork(event.target.value)}
                className="h-10 rounded-md border border-border bg-card px-3 text-sm focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary"
              />
            </div>
          ) : null}
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="edu-message" className={labelClass}>
            문의 내용
          </label>
          <textarea
            id="edu-message"
            name="message"
            rows={5}
            placeholder="현재 어떤 업무에 시간이 가장 많이 드는지 적어 주시면 도움이 됩니다."
            className="rounded-md border border-border bg-card p-3 text-sm focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary"
          />
        </div>

        <InquiryConsent agreed={agreed} onChange={setAgreed} />

        {/* 실패를 삼키지 않는다 — 조용히 넘기면 사용자는 접수된 줄 알고 기다린다. */}
        {submitInquiry.error ? (
          <p role="alert" className="text-[13px] text-destructive">
            {toMessage(submitInquiry.error)}
          </p>
        ) : null}

        {/* 보내는 중이라는 사실을 글자로도 알린다 — 잠긴 버튼만으로는 화면을 못 보는
            사용자에게 아무 신호도 가지 않는다. 실제 중복 차단은 onSubmit 이 한다. */}
        <Button type="submit" size="lg" disabled={!agreed} aria-disabled={submitInquiry.isPending}>
          {submitInquiry.isPending ? '보내는 중…' : '문의 보내기'}
        </Button>
      </form>
    </div>
  );
}
